import { AssociateServicesModalProps } from "./types";
import React, { useState } from "react";
import { ServiceClass } from "../../types";
import Modal from "@ui-components/Modal";
import SearchSelect from "@ui-components/SearchSelect";
import { FormattedMessage, useIntl } from "react-intl";
import { XMarkIcon } from "@heroicons/react/24/solid";
import Badge from "@ui-components/Badge";
import { useServices } from "../hooks/useServices";
import Button from "@ui-components/Button";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import Alert from "@ui-components/Alert";
import Toggle from "@ui-components/Toggle";
import {PRIVILEGED_ROLES} from "../../../constants";

export const AssociateServicesModal = ({
  service,
  association,
  onConfirm,
  onClose,
}: AssociateServicesModalProps) => {
  // hooks
  const intl = useIntl();
  const { services, update, loading } = useServices();

  // state
  const [selected, setSelected] = useState(association);

  const options =
    services
      ?.filter(
        ({ class: serviceClass }) => serviceClass === ServiceClass.QUOTATION
      )
      ?.map(({ id, name }) => ({
        label: name,
        value: id,
      }))
      ?.sort((a, b) => a.label.localeCompare(b.label)) ?? [];
  const showWarning =
    !!association && (!selected || selected.id !== association.id);

  const save = async () => {
    if (selected) {
      return await update(selected.id, selected);
    } else if (association) {
      const { [service.id]: _, ...associated_services } = association.associated_services;
      await update(
        association?.id,
        {
          ...association,
          associated_services,
        }
      );
      return undefined;
    }
    return undefined;
  };

  return (
    // @ts-ignore
    <Modal onExit={onClose}>
      <h2 className="text-2xl">
        <FormattedMessage id="services_association" />
      </h2>
      <div className="flex flex-col gap-1 my-5">
        {!!selected ? (
          <>
            <p className="text-gray-00">
              <FormattedMessage id="associated_service" />
            </p>
            <div className="bg-gray-50 border border-gray-300 px-3 py-2 flex items-center justify-between">
              <div className="flex-1">
                <p className="font-medium mb-1">{selected.name}</p>
                <Badge
                  type="success"
                  text={<FormattedMessage id="quotation_service" />}
                  textSize="text-xs uppercase mb-0.5"
                  onClick={undefined}
                />
              </div>
              <XMarkIcon
                className="w-6 cursor-pointer"
                onClick={() => {
                  setSelected(undefined);
                }}
              />
            </div>
            {/*@ts-ignore*/}
            <Toggle
              label={
                <FormattedMessage
                  id="is_public_association"
                  defaultMessage={"Is public association"}
                />
              }
              checked={
                selected.associated_services[service.id]?.r_perm?.includes(
                  "public"
                ) ?? true
              }
              onChange={async () => {
                const newService = {
                  ...selected,
                  associated_services: {
                    ...selected.associated_services,
                    [service.id]: {
                      ...selected.associated_services[service.id],
                      r_perm: selected.associated_services[
                        service.id
                      ]?.r_perm?.includes("public")
                        // If association in not public, only admin and CS can see it (i.e. can convert design surveys
                        // into quotation surveys before they are public)
                        ? PRIVILEGED_ROLES.filter((r) => r !== "sales")
                        : ["public"],
                    },
                  },
                };
                setSelected(newService);
              }}
            />
          </>
        ) : (
          <>
            {/*@ts-ignore*/}
            <SearchSelect
              label={intl.formatMessage({
                id: "select_quotation_service_prompt",
              })}
              options={options}
              value={options.find((o) => o.value === selected?.id)}
              onChange={({ value }: { value: number }) => {
                const found = services?.find((s) => s.id === value);
                if (!found) {
                  return;
                }
                if (!found.associated_services[service.id]) {
                  found.associated_services = {
                    ...found.associated_services,
                    [service.id]: {
                      r_perm: ["admin", "CS", "field"],
                    },
                  };
                }
                setSelected(found);
              }}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
              }}
            />
            <div className="flex flex-row gap-2 items-start text-gray-500">
              <InformationCircleIcon className="w-4 my-0.5 flex-shrink-0" />
              <span className="text-xs">
                <FormattedMessage id="select_quotation_service_hint" />
              </span>
            </div>
          </>
        )}
        {showWarning && (
          <div className="my-3">
            {/*@ts-ignore*/}
            <Alert
              type="warning"
              title={<FormattedMessage id="warning" />}
              text={<FormattedMessage id="edit_quotation_service_hint" />}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Button full styleType="empty" onClick={onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          full
          onClick={async () => {
            const savedAssociation = await save();
            onConfirm(savedAssociation);
          }}
          submitting={loading}
          disabled={
            // confirm button is disabled if
            // 1. the existing association didn't change
            // 2. there was no previous association and no new association is selected
            !selected && !association
          }
        >
          <FormattedMessage id="save" />
        </Button>
      </div>
    </Modal>
  );
};
